import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { GrundstueckId } from '../../../../core/gb-api';
import {
  AvgeometergeschaeftService,
  GeometergeschaeftListEntryMeta,
  LoadGeoGeschaeftSichtByGrundstueckIdsRequest
} from '../../../../core/av-api';
import { keysToTranslate } from 'i18n/keys-to-translate';
import { AbstractComponent } from '../../abstract-component/abstract.component';
import { ListLoader } from '../../list-loader/list-loader';
import { ListLoaderPaginationBuilder } from '../../list-loader/list-loader-pagination';
import { map } from 'rxjs/operators';
import { PaginationLookupResult } from '../../list-loader/pagination-lookup-result';
import { SortDirection, TableSortModel } from '../../../tablesort/tableSortModel';
import { ComparatorService } from '../../../services/comparator-service';
import { AppPermissionService } from '../../../../core/services/app-permission.service';
import { FormatConfigService } from '../../../../core/services/format-config.service';
import { ErweiterungConfigResponse } from '../../../../core/base-api';
import { ListLoaderWrapperComponent } from '../../list-loader-wrapper/list-loader-wrapper.component';
import { NgTemplateOutlet } from '@angular/common';
import { ColumnHeaderComponent } from '../../../tablesort/sort-icon/column-header.component';
import { KompaktGeometergeschaeftComponent } from '../../../kompaktdarstellung/geometergeschaeft/kompakt-geometergeschaeft.component';
import { LinkComponent } from '../../link/link.component';
import { KompaktAvMutationComponent } from '../../../kompaktdarstellung/av-mutation/kompakt-av-mutation.component';
import { TranslatePipe } from '@ngx-translate/core';
import { DatumPipe } from '../../../pipes/simple/datum.pipe';
import SortEnum = LoadGeoGeschaeftSichtByGrundstueckIdsRequest.SortEnum;
import ActiveErweiterungenEnum = ErweiterungConfigResponse.ActiveErweiterungenEnum;

@Component({
  selector: 'cap-haengige-geo-geschaefte',
  templateUrl: './haengige-geo-geschaefte.component.html',
  styleUrls: ['./haengige-geo-geschaefte.component.scss'],
  imports: [
    ListLoaderWrapperComponent,
    NgTemplateOutlet,
    ColumnHeaderComponent,
    KompaktGeometergeschaeftComponent,
    LinkComponent,
    KompaktAvMutationComponent,
    TranslatePipe,
    DatumPipe
  ]
})
export class HaengigeGeoGeschaefteComponent extends AbstractComponent implements OnInit, OnDestroy, OnChanges {
  readonly PAGE_SIZE = 5;
  readonly CURRENT_PAGE = 1;

  @Input()
  perimeter: Array<GrundstueckId>;
  @Input()
  hasRight: boolean = true;

  haengigeLoader: ListLoader<GeometergeschaeftListEntryMeta>;
  tableSortModel = new TableSortModel<GeometergeschaeftListEntryMeta>('table', this.comparatorService);
  canViewGeoGeschaeftDetails: boolean = false;
  avGeschaeftErweiterung: boolean;

  constructor(
    private geometergeschaeftService: AvgeometergeschaeftService,
    private comparatorService: ComparatorService,
    private permissionService: AppPermissionService,
    private formatConfigService: FormatConfigService
  ) {
    super();
    keysToTranslate<string>(['AvMutation', 'DatumAuftrag']);
    keysToTranslate<GeometergeschaeftListEntryMeta.StatusEnum>([
      'EROEFFNET',
      'BEARBEITUNG',
      'BEREIT_ZUR_VERIFIKATION',
      'WARTEN_AUF_VORGAENGER',
      'BEREIT_FUER_GRUNDBUCH',
      'RECHTSGUELTIG_VOLLZOGEN',
      'RECHTSGUELTIG_ABGESCHLOSSEN',
      'ZURUECK_ZUR_KORREKTUR',
      'ZURUECK_ZURUECKGEZOGEN',
      'ZURUECK_ABGEWIESEN',
      'RUECKMUTIERT_ABGESCHLOSSEN',
      'BEREIT_FUER_VERARBEITUNG',
      'VERKNUEPFT_MIT_GRUNDBUCH',
      'BEREIT_FUER_KORREKTURLIEFERUNG'
    ]);
    this.tableSortModel.addColumn('nummer', undefined, SortEnum.NUMMER).changeSortMode(SortDirection.DESC);
    this.tableSortModel.addColumn('status', undefined, SortEnum.STATUS);
    this.tableSortModel.addColumn('auftragsDatum', undefined, SortEnum.AUFTRAG_DATUM);
    this.tableSortModel.addColumn('avMutation', undefined, SortEnum.AV_MUTATION);
    this.tableSortModel.addColumn('geoGeschaeftsArt', undefined, SortEnum.GESCHAEFT_ART);

    this.addSub(
      this.formatConfigService.erweiterungsConfig$.subscribe((config) => {
        this.avGeschaeftErweiterung = config?.activeErweiterungen.includes(ActiveErweiterungenEnum.AV_GESCHAEFT_INTERCAPI);
      })
    );
  }

  private _loadOption;

  get loadOption() {
    return this._loadOption;
  }

  @Input()
  set loadOption(value: string) {
    if (!value) {
      return;
    }
    this._loadOption = value;
  }

  ngOnInit(): void {
    this.addSub(
      this.formatConfigService.erweiterungsConfig$.subscribe((config) => {
        const hasPermission: boolean = this.permissionService.hasPermission('GEOMETER_REGISTER');
        this.canViewGeoGeschaeftDetails =
          hasPermission && config?.activeErweiterungen.includes(ActiveErweiterungenEnum.AV_GESCHAEFT_INTERCAPI);
      })
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.perimeter?.currentValue || this.perimeter) {
      this.updateLoader();
    }
  }

  ngOnDestroy(): void {
    for (const subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
    if (this.haengigeLoader) {
      this.haengigeLoader.unsubscribeAll();
    }
  }

  private updateLoader(): void {
    if (!this.hasRight || !this.perimeter) {
      return;
    }
    if (this.haengigeLoader) {
      this.haengigeLoader.unsubscribeAll();
    }
    if (this.perimeter.length === 1) {
      this.haengigeLoader = this.createGeoGeschaeftByGrundstueckIdLoader();
    } else {
      this.haengigeLoader = this.createGeoGeschaeftByGrundstueckIdsLoader();
    }
    if (this.haengigeLoader) {
      this.haengigeLoader.applySortModel(this.tableSortModel);
    }
  }

  private createGeoGeschaeftByGrundstueckIdLoader(): ListLoader<GeometergeschaeftListEntryMeta> {
    const observable = (offset, limit, sortColumn) => this.createGeoGeschaeftByGrundstueckIdObservable(offset, limit, sortColumn);
    const loader = ListLoaderPaginationBuilder.builder<GeometergeschaeftListEntryMeta>()
      .setLookupItemFunc(observable)
      .setCurrentPage(this.CURRENT_PAGE)
      .setPageSize(this.PAGE_SIZE)
      .build();
    this.addLoader(loader);
    return loader;
  }

  private createGeoGeschaeftByGrundstueckIdObservable(offset, limit, sortColumn) {
    return this.geometergeschaeftService
      .loadGeometergeschaeftSichtGrundstueckGet(
        this.perimeter[0].id,
        offset,
        limit,
        sortColumn.sortDirectionBackend,
        sortColumn.sortEnumBackend,
        this._loadOption
      )
      .pipe(
        map(
          (r) =>
            <PaginationLookupResult<GeometergeschaeftListEntryMeta>>{
              result: r.results,
              totalItems: r.totalItems
            }
        )
      );
  }

  private createGeoGeschaeftByGrundstueckIdsLoader(): ListLoader<GeometergeschaeftListEntryMeta> {
    const observable = (offset, limit, sortColumn) => this.createGeoGeschaeftByGrundstueckIdsObservable(offset, limit, sortColumn);
    const loader = ListLoaderPaginationBuilder.builder<GeometergeschaeftListEntryMeta>()
      .setLookupItemFunc(observable)
      .setCurrentPage(this.CURRENT_PAGE)
      .setPageSize(this.PAGE_SIZE)
      .build();
    this.addLoader(loader);
    return loader;
  }

  private createGeoGeschaeftByGrundstueckIdsObservable(offset, limit, sortColumn) {
    const request: LoadGeoGeschaeftSichtByGrundstueckIdsRequest = {
      grundstueckIds: this.perimeter,
      auftragLoadOption: 'HAENGIGE',
      offset: offset,
      limit: limit,
      sortDirection: sortColumn.sortDirectionBackend,
      sort: sortColumn.sortEnumBackend
    };
    return this.geometergeschaeftService.loadGeometergeschaeftSichtGrundstueckPost(request).pipe(
      map(
        (r) =>
          <PaginationLookupResult<GeometergeschaeftListEntryMeta>>{
            result: r.results,
            totalItems: r.totalItems
          }
      )
    );
  }
}
